import CachedIcon from "mdi-react/CachedIcon";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import { useState } from "react";
import { Button, Table, Dropdown } from "react-bootstrap";
import { useAuth } from "../../hooks/useAuth";
import { useQueryParams, useScrollTop } from "../../utils/hooks";
import { ChartPieSliceTitleIcon, DeleteIcon, EditIcon } from "../Icons";
import PageHeader from "../PageHeader";
import RsDateRangePicker from "../utils/RsDateRangePicker";
import AddIncentiveModal from "./AddIncentiveModal";
import AddPromoIncentiveModal from "./AddPromoIncentiveModal";
import queryString from "query-string";
import ReactPaginate from "react-paginate";
import {
  fetchActionsUtil,
  paginationOptions,
  scrollToTop,
} from "../../utils/helpers";
import { format, parse, startOfMonth } from "date-fns";
import { useMutation, useQuery, useQueryClient } from "react-query";
import DotsHorizontalIcon from "mdi-react/DotsHorizontalIcon";
import ConfirmDialog from "../ConfirmDialogue";
import { toast } from "react-toastify";

export default function Incentive() {
  const initialFilterParams = {
    // startDate: format(startOfMonth(new Date()), "yyyy-MM-dd"),
    // endDate: format(new Date(), "yyyy-MM-dd"),
  };

  const { backendUrl } = useAuth();
  const queryClient = useQueryClient();
  useScrollTop();
  const [showCreateIncentiveModal, setShowCreateIncentiveModal] = useState(
    false
  );
  const [
    showCreatePromoIncentiveModal,
    setShowCreatePromoIncentiveModal,
  ] = useState(false);
  const [editingIncentive, setEditingIncentive] = useState(null);

  const [queryParams, setQueryParams] = useQueryParams({
    page: 1,
    limit: 40,
    ...initialFilterParams,
  });
  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });

  const fetchIncentives = async queryParams => {
    // await waitFor(5000);
    let response = await fetch(
      `${backendUrl}/api/incentive?&${queryString.stringify(queryParams)}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();
    return data;
  };

  const { data = { count: 0, incentives: [] }, refetch } = useQuery(
    ["GET_INCENTIVES", queryParams],
    () => fetchIncentives(queryParams),
    {
      keepPreviousData: true,
    }
  );

  const handleFilterParamsChange = e => {
    setFilterParams({
      ...filterParams,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });
  };

  const handleSearchQueryChange = e => {
    setQueryParams({
      ...queryParams,
      [e.target.name]: e.target.value,
    });
  };

  const filterByDateRange = date => {
    setQueryParams({
      ...queryParams,
      startDate: format(date[0], "yyyy-MM-dd"),
      endDate: format(date[1], "yyyy-MM-dd"),
    });
  };

  const clearDateRange = () => {
    setQueryParams({
      ...queryParams,
      startDate: "",
      endDate: "",
    });
  };

  const deleteAccount = useMutation(
    payload =>
      fetchActionsUtil(`${backendUrl}/api/incentive/${payload.id}`, "DELETE"),
    {
      onSuccess: () => {
        refetch();
        toast.success("Deleted");
      },
      onError: () => {
        toast.error("Unable to delete");
      },
    }
  );

  const deleteIncentive = async incentive => {
    if (
      await ConfirmDialog({
        type: "danger",
        title: "Delete Incentive",
        description: `Are you sure you want to delete incentive`,
      })
    ) {
      deleteAccount.mutate(incentive);
    }
  };

  const editIncentive = incentive => setEditingIncentive(incentive);

  const splitText = text => {
    return text ? text.split("&&&").join(", ") : "_";
  };

  const thresholdByLabels = {
    qty: "Units (Bags, Each)",
    line: "Per Purchase line entry (Truck, ATC)",
    amount: "Total Amount",
  };

  const claimByLabels = {
    qty: "Units (Bags, Each)",
    amount_per_qty: "Amount per Unit",
    line: "Purchase line entry (Truck, ATC)",
    fixed_total: "Fixed Amount",
  };

  const isPromo = el => el.IncentiveType === "Promo";

  return (
    <>
      <PageHeader
        name="ATC Incentives Setup"
        description=""
        icon={<ChartPieSliceTitleIcon />}
      />

      <main className="cash-book">
        <div className="content">
          <header>
            <div className="actions mr-5 gap-3">
              <div
                className="global-search-area w-50"
                style={{ height: "2.5rem", minWidth: "25vw" }}
              >
                <MagnifyIcon />
                <input
                  className="form-control search-input"
                  name="q"
                  placeholder="Search..."
                  autoFocus
                  autoComplete="off"
                  value={filterParams?.q}
                  onChange={e => handleFilterParamsChange(e)}
                />
              </div>
              <RsDateRangePicker
                placement="bottomEnd"
                defaultValue={
                  queryParams.startDate && queryParams.endDate
                    ? [
                        parse(queryParams.startDate, "yyyy-MM-dd", new Date()),
                        parse(queryParams.endDate, "yyyy-MM-dd", new Date()),
                      ]
                    : []
                }
                onClean={() => clearDateRange()}
                onOk={date => filterByDateRange(date)}
              />
              <button onClick={() => refetch()} className="btn text-primary">
                <CachedIcon />
              </button>
            </div>

            <Dropdown>
              <Dropdown.Toggle className="text-nowrap">
                + New Incentive
              </Dropdown.Toggle>
              <Dropdown.Menu
                popperConfig={{
                  strategy: "fixed",
                }}
                renderOnMount
              >
                <Dropdown.Item
                  as="button"
                  onClick={() => setShowCreateIncentiveModal(true)}
                >
                  Regular
                </Dropdown.Item>
                <Dropdown.Item
                  as="button"
                  onClick={() => setShowCreatePromoIncentiveModal(true)}
                >
                  Promo
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </header>

          <div className="content-main">
            <div className="content-body">
              <div className="px-4">
                <Table
                  responsive
                  borderless
                  striped
                  className="product-table load-atc text-nowrap"
                >
                  <thead>
                    <tr>
                      <th />
                      <th>Name</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Sub Category</th>
                      <th>Region</th>
                      <th>Depot / Plant</th>
                      <th>Reward</th>
                      <th>Expiry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.incentives.map((el, index) => (
                      <tr key={index}>
                        <td>
                          <Dropdown style={{ margin: 0 }}>
                            <Dropdown.Toggle
                              variant=""
                              className="bg-light-blue text-primary"
                              bsPrefix="print more"
                            >
                              <DotsHorizontalIcon />
                            </Dropdown.Toggle>

                            <Dropdown.Menu
                              popperConfig={{
                                strategy: "fixed",
                              }}
                              renderOnMount
                              className="dropdown-with-icons"
                            >
                              <Dropdown.Item
                                as="button"
                                onClick={() => editIncentive(el)}
                              >
                                <EditIcon /> Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                as="button"
                                onClick={() => deleteIncentive(el)}
                              >
                                <DeleteIcon /> Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                        <td>{el.Name || "_"}</td>
                        <td>
                          <span
                            className={`badge ${
                              isPromo(el)
                                ? "bg-light text-dark"
                                : "bg-light-blue text-primary"
                            }`}
                          >
                            {isPromo(el) ? "Promo" : "Regular"}
                          </span>
                        </td>
                        <td>{splitText(el.Category)}</td>
                        <td>{splitText(el.SubCategory || "_")}</td>
                        <td>{splitText(el.Region || "_")}</td>
                        <td>{splitText(el.PlantOrDepot || "_")}</td>
                        <td>
                          {isPromo(el) ? (
                            <>
                              Buy {el.Threshold || 0}{" "}
                              {thresholdByLabels[el.ThresholdBy] ||
                                el.ThresholdBy}{" "}
                              → Get {el.Claim || 0}{" "}
                              {claimByLabels[el.ClaimBy] || el.ClaimBy}
                            </>
                          ) : (
                            <>
                              {el.Amount || "_"} {el.RewardBy}
                            </>
                          )}
                        </td>
                        <td>
                          {el.ExpiryDate
                            ? format(new Date(el.ExpiryDate), "dd MMM yyyy")
                            : "_"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="d-flex justify-content-between px-3 align-items-center pagination">
                {/*<p className="m-0">
                Showing {data?.startIndex + 1} to{" "}
                {data?.endIndex <= data?.count ? data?.endIndex : data?.count}{" "}
                of {data.count} entries
                  </p> */}
                <div className="pagination_left">
                  <p className="m-0 p-0">Show</p>
                  <select
                    value={queryParams.limit}
                    name="limit"
                    className="form-select "
                    onChange={e => handleSearchQueryChange(e)}
                  >
                    <option value="10">10 rows</option>
                    <option value="20">20 rows</option>
                    <option value="30">30 rows</option>
                    <option value="40">40 rows</option>
                    <option value="50">50 rows</option>
                    <option value="100">100 rows</option>
                  </select>
                </div>

                <ReactPaginate
                  {...paginationOptions}
                  pageCount={Math.ceil(data.count / queryParams.limit)}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={0}
                  onPageChange={({ selected }) => {
                    scrollToTop();
                    setQueryParams({
                      ...queryParams,
                      page: selected + 1,
                    });
                  }}
                  forcePage={queryParams.page - 1}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {showCreateIncentiveModal && (
        <AddIncentiveModal
          setShowCreateIncentiveModal={setShowCreateIncentiveModal}
          refetch={refetch}
        />
      )}

      {showCreatePromoIncentiveModal && (
        <AddPromoIncentiveModal
          setShowCreatePromoIncentiveModal={setShowCreatePromoIncentiveModal}
          reloadTable={refetch}
        />
      )}

      {editingIncentive && !isPromo(editingIncentive) && (
        <AddIncentiveModal
          incentive={editingIncentive}
          setShowCreateIncentiveModal={() => setEditingIncentive(null)}
          reloadTable={refetch}
        />
      )}

      {editingIncentive && isPromo(editingIncentive) && (
        <AddPromoIncentiveModal
          incentive={editingIncentive}
          setShowCreatePromoIncentiveModal={() => setEditingIncentive(null)}
          reloadTable={refetch}
        />
      )}
    </>
  );
}
